import React, { useEffect, useState } from "react";
import { Gamepad2, ExternalLink, Eye, MousePointerClick, CheckCircle2, Users, Trophy, Bell } from "lucide-react";
import api from "../lib/api";
import { useAppSettings } from "../context/AppSettingsContext";
import { toast } from "sonner";

function ContestAdminExtras() {
  const [board, setBoard] = useState(null);
  const [history, setHistory] = useState([]);
  const [sending, setSending] = useState(false);
  useEffect(() => { api.get("/contest/leaderboard").then((r) => setBoard(r.data)).catch(() => {}); api.get("/contest/history").then((r) => setHistory(r.data || [])).catch(() => {}); }, []);
  const closeWeek = async () => {
    try { const { data } = await api.post("/contest/close-week?force=true"); toast.success(data.winner ? `Settimana chiusa: vince ${data.winner.full_name || data.winner.name}` : "Settimana chiusa (nessun partecipante)"); const h = await api.get("/contest/history"); setHistory(h.data || []); }
    catch (e) { toast.error(e?.response?.data?.detail || "Operazione non riuscita"); }
  };
  const notify = async () => {
    setSending(true);
    try { const { data } = await api.post("/contest/notify"); toast.success(`Push inviata a ${data.sent} dispositivi`); }
    catch (e) { toast.error(e?.response?.data?.detail || "Invio non riuscito"); } finally { setSending(false); }
  };
  return (
    <div className="grid gap-4 md:grid-cols-[1fr_280px]">
      <div className="card-elev p-5" data-testid="concorsi-leaderboard">
        <div className="flex items-center gap-2 mb-3"><Trophy className="h-4 w-4 text-amber-400" /><h2 className="font-heading font-bold">Classifica settimanale</h2>{board && <span className="text-xs text-zinc-500">dal {board.week_start}</span>}</div>
        {!board?.top?.length ? <p className="text-sm text-zinc-500" data-testid="concorsi-leaderboard-empty">Nessuna partecipazione questa settimana.</p> : (
          <ol className="space-y-1.5">
            {board.top.map((r) => (
              <li key={r.rank} className="flex items-center gap-3 text-sm rounded-lg bg-zinc-950 border border-zinc-800 px-3 py-2" data-testid={`concorsi-leaderboard-row-${r.rank}`}>
                <span className={`h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold ${r.rank === 1 ? "bg-amber-400 text-black" : "bg-zinc-800 text-zinc-300"}`}>{r.rank}</span>
                <span className="flex-1 truncate">{r.name || "—"} <span className="text-zinc-500 font-mono text-xs">{r.card_number}</span></span>
                <span className="text-xs text-zinc-400">{r.plays} partecipazioni</span>
              </li>
            ))}
          </ol>
        )}
      </div>
      <div className="card-elev p-5 space-y-2" data-testid="concorsi-notify">
        <div className="flex items-center gap-2"><Bell className="h-4 w-4 text-blue-400" /><h2 className="font-heading font-bold">Avvisa i clienti</h2></div>
        <p className="text-xs text-zinc-500">Push a tutti i dispositivi iscritti con link diretto al gioco. Viene inviata in automatico quando cambi l'URL del concorso in Personalizza.</p>
        <button type="button" onClick={notify} disabled={sending} className="w-full rounded-full py-2 text-sm font-semibold bg-[color:var(--paprika)] hover:bg-[color:var(--paprika-hover)] text-white disabled:opacity-50" data-testid="concorsi-notify-send">{sending ? "Invio…" : "Invia push ora"}</button>
        <div className="pt-3 mt-3 border-t border-zinc-800 space-y-2" data-testid="concorsi-history">
          <div className="flex items-center justify-between"><span className="text-xs font-semibold text-zinc-300">Storico settimane</span><button type="button" onClick={closeWeek} className="text-[11px] text-blue-300 hover:underline" data-testid="concorsi-close-week">Chiudi settimana scorsa ora</button></div>
          {history.length === 0 ? <p className="text-[11px] text-zinc-500">Nessuna settimana archiviata: la chiusura avviene ogni lunedì alle 08:00.</p> : history.slice(0, 6).map((w) => (
            <div key={w.week_start} className="text-[11px] text-zinc-400 flex justify-between gap-2" data-testid={`concorsi-history-${w.week_start}`}><span>{w.week_start} → {w.week_end}</span><span className="text-zinc-200 truncate">🏆 {w.winner ? `${w.winner.full_name || w.winner.name} · ${w.winner.card_number}` : "—"}{w.coupon_id ? " · coupon" : ""}{w.wa_sent ? " · WA" : ""}</span></div>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Sezione Concorsi (admin) — mostra il gioco esterno via iframe e le statistiche
 * di partecipazione (open/click esterno/completamento) su una finestra scelta.
 * L'URL è configurabile dalle Impostazioni App (`contest_url`).
 * `allow="camera"` è necessario se il gioco chiede l'accesso alla fotocamera.
 */
export default function Concorsi() {
  const { settings } = useAppSettings();
  const GAME_URL = settings?.contest_url || "";
  const TITLE = settings?.contest_title || "Concorsi";
  const [statsWindow, setStatsWindow] = useState(30);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get(`/contest/stats`, { params: { days: statsWindow } })
      .then(({ data }) => { if (alive) setStats(data); })
      .catch(() => { if (alive) setStats(null); });
    return () => { alive = false; };
  }, [statsWindow]);

  return (
    <div className="space-y-4 animate-fade-up" data-testid="concorsi-page">
      <div className="flex flex-col sm:flex-row justify-between gap-3">
        <div>
          <span className="pill pill-gold"><Gamepad2 className="h-3 w-3" /> CONCORSO</span>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold mt-3">{TITLE}</h1>
          <p className="text-zinc-400 mt-1">
            Gioca al concorso attivo e prova a vincere premi extra oltre alla tua carta fedeltà.
          </p>
        </div>
        {GAME_URL && (
          <a
            href={GAME_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full border border-amber-500/40 text-amber-300 hover:bg-amber-500/10 self-start"
            data-testid="concorsi-open-external"
          >
            <ExternalLink className="h-3 w-3" /> Apri in una nuova scheda
          </a>
        )}
      </div>

      {/* Classifica settimanale + push */}
      <ContestAdminExtras />

      {/* Stats concorso */}
      <div className="card-elev p-5" data-testid="concorsi-stats">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-sm text-zinc-200">Statistiche partecipazione</h3>
          <div className="flex items-center gap-2 text-[11px]">
            {[7, 30, 90].map((d) => (
              <button
                key={d}
                onClick={() => setStatsWindow(d)}
                data-testid={`concorsi-stats-window-${d}`}
                className={`px-2 py-1 rounded ${statsWindow === d ? "bg-amber-500/20 text-amber-300 border border-amber-500/40" : "text-zinc-500 border border-zinc-800 hover:border-zinc-600"}`}
              >
                {d}g
              </button>
            ))}
          </div>
        </div>
        {stats ? (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <StatCard icon={Eye} label="Aperture" value={stats.opens} color="#60A5FA" testid="concorsi-stat-opens" />
            <StatCard icon={MousePointerClick} label="Click esterni" value={stats.external_clicks} color="#F59E0B" testid="concorsi-stat-external" />
            <StatCard icon={CheckCircle2} label="Completamenti" value={stats.completes} color="#22C55E" testid="concorsi-stat-completes" />
            <StatCard icon={Users} label="Clienti unici" value={stats.unique_customers} color="#A78BFA" testid="concorsi-stat-unique" />
            <StatCard label="Tasso completamento" value={`${stats.completion_rate_pct}%`} color="#F472B6" testid="concorsi-stat-rate" />
          </div>
        ) : (
          <div className="text-center py-4 text-zinc-500 text-xs">Nessun dato ancora — condividi il concorso con i clienti per iniziare a tracciare le partecipazioni.</div>
        )}
        {stats && stats.series && stats.series.length > 0 && (
          <div className="mt-4 border-t border-zinc-800 pt-3">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 mb-2">Andamento giornaliero</div>
            <div className="flex items-end gap-1 h-16">
              {stats.series.slice(-30).map((d) => {
                const max = Math.max(...stats.series.map((x) => x.opens + x.completes), 1);
                const h = ((d.opens + d.completes) / max) * 100;
                return (
                  <div key={d.date} className="flex-1 flex flex-col justify-end gap-0.5" title={`${d.date}: ${d.opens} aperture, ${d.completes} completamenti`}>
                    <div className="w-full bg-emerald-500/60 rounded-sm" style={{ height: `${(d.completes / max) * 100}%` }} />
                    <div className="w-full bg-blue-500/40 rounded-sm" style={{ height: `${(d.opens / max) * 100 - (d.completes / max) * 100}%` }} />
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-3 mt-2 text-[10px] text-zinc-500">
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-blue-500/60" /> Aperture</span>
              <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500/60" /> Completamenti</span>
            </div>
          </div>
        )}
      </div>

      {GAME_URL ? (
        <div className="card-elev p-0 overflow-hidden" data-testid="concorsi-embed-card">
          <iframe
            src={GAME_URL}
            title={`Concorso ${TITLE}`}
            data-testid="concorsi-iframe"
            width="100%"
            height="820"
            style={{ border: 0, borderRadius: 16, display: "block" }}
            allow="camera"
          />
        </div>
      ) : (
        <div className="card-elev p-10 text-center" data-testid="concorsi-empty">
          <Gamepad2 className="h-10 w-10 mx-auto mb-3 text-zinc-600" />
          <div className="text-zinc-300 font-semibold mb-1">Nessun concorso configurato</div>
          <p className="text-xs text-zinc-500">
            Imposta l'URL del gioco/concorso in <b>Impostazioni → Concorso</b> per attivarlo per amministratori e clienti.
          </p>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="rounded-xl bg-zinc-950 border border-zinc-800 p-3" data-testid={testid}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-zinc-500 mb-1">
        {Icon && <Icon className="h-3 w-3" style={{ color }} />} {label}
      </div>
      <div className="font-heading text-xl font-bold" style={{ color }}>{value}</div>
    </div>
  );
}
